<template>
  <li
    v-if="canViewVerticalNavMenuLink(item)"
    class="nav-item nav-link-with-favorite"
    :class="{
      'active': isActive,
      'disabled': item.disabled,
      'is-favorited': isFavorite
    }"
  >
    <!-- Favorite star trigger - positioned absolutely over the icon -->
    <span
      v-if="showFavoriteStar"
      v-b-tooltip.hover.right="favoriteTooltip"
      class="favorite-star-trigger"
      @click.prevent.stop="handleFavoriteClick"
    >
      <feather-icon
        icon="StarIcon"
        :class="['favorite-star-icon', { 'is-favorite': isFavorite }]"
      />
    </span>
    <b-link
      v-bind="linkProps"
      class="d-flex align-items-center"
    >
      <feather-icon :icon="item.icon || 'CircleIcon'" />
      <span class="menu-title text-truncate">{{ t(item.title) }}</span>
      <b-badge
        v-if="item.tag"
        pill
        :variant="item.tagVariant || 'primary'"
        class="mr-1 ml-auto"
      >
        {{ item.tag }}
      </b-badge>
    </b-link>
  </li>
</template>

<script>
import { useUtils as useAclUtils } from '@core/libs/acl'
import { BLink, BBadge, VBTooltip } from 'bootstrap-vue'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import { computed, inject, ref } from '@vue/composition-api'
import FavoriteService from '@/helpers/favoriteService'
import useVerticalNavMenuLink from './useVerticalNavMenuLink'
import mixinVerticalNavMenuLink from './mixinVerticalNavMenuLink'

export default {
  components: {
    BLink,
    BBadge,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [mixinVerticalNavMenuLink],
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { isActive, linkProps, updateIsActive } = useVerticalNavMenuLink(props.item)
    const { t } = useI18nUtils()
    const { canViewVerticalNavMenuLink } = useAclUtils()

    // Inject favorites state from parent
    const favoritesCount = inject('favoritesCount', ref(0))

    // Check if this item is already a favorite
    // Note: we reference favoritesCount.value to trigger re-computation when favorites change
    const isFavorite = computed(() => {
      if (!props.item.route) return false
      // eslint-disable-next-line no-unused-expressions
      favoritesCount.value // trigger reactivity when count changes
      return FavoriteService.isFavorite(props.item.route)
    })

    // Show star only if: item has a route AND (not at limit OR already a favorite)
    const showFavoriteStar = computed(() => {
      // Must have a route to be favoritable
      if (!props.item.route) return false
      const atLimit = favoritesCount.value >= 10
      return !atLimit || isFavorite.value
    })

    // Tooltip text
    const favoriteTooltip = computed(() => {
      if (isFavorite.value) {
        return 'Remove from Favourites'
      }
      return 'Add to Favourites'
    })

    // Handle star click - emit event to parent
    const handleFavoriteClick = () => {
      emit('toggle-favorite', props.item, isFavorite.value)
    }

    return {
      isActive,
      linkProps,
      updateIsActive,

      // ACL
      canViewVerticalNavMenuLink,

      // i18n
      t,

      // Favorites
      isFavorite,
      showFavoriteStar,
      favoriteTooltip,
      handleFavoriteClick,
    }
  },

}
</script>

<style lang="scss">
// Star overlay for favorites - positioned over the icon
.nav-link-with-favorite {
  position: relative;

  // Star trigger - absolutely positioned over the icon area
  > .favorite-star-trigger {
    position: absolute;
    left: 15px; // Match the link padding
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;

    .favorite-star-icon {
      color: #b4b7bd;
      transition: color 0.2s ease, transform 0.2s ease;
      height: 18px;
      width: 18px;

      // Filled yellow star for favorited items
      &.is-favorite {
        color: #ffc107 !important;
        fill: #ffc107 !important;
      }
    }

    &:hover .favorite-star-icon {
      transform: scale(1.15);
      color: #ffc107;
    }
  }

  // For items that ARE favorites: always show filled star, hide CircleIcon/dot
  &.is-favorited {
    > .favorite-star-trigger {
      opacity: 1 !important;
    }
    > a > svg:first-of-type {
      opacity: 0 !important;
      visibility: hidden;
    }
  }

  // On hover for non-favorites: show star, hide original icon
  &:not(.is-favorited):hover {
    > .favorite-star-trigger {
      opacity: 1;
    }
    > a > svg:first-of-type {
      opacity: 0;
    }
  }
}

// Sub-menu items (all levels) - smaller star, adjusted position
.menu-content,
.sub-items {
  .nav-link-with-favorite {
    > .favorite-star-trigger {
      left: 30px !important; // Sub-items have more left padding
      width: 14px !important;
      height: 14px !important;

      .favorite-star-icon {
        height: 14px !important;
        width: 14px !important;
        min-width: 14px;
        min-height: 14px;
      }
    }

    // Ensure favorites show filled star in sub-menus too
    &.is-favorited {
      > .favorite-star-trigger {
        opacity: 1 !important;
      }
      > a > svg:first-of-type {
        opacity: 0 !important;
        visibility: hidden;
      }
    }

    // Hover state for sub-menu items
    &:not(.is-favorited):hover {
      > .favorite-star-trigger {
        opacity: 1 !important;
      }
      > a > svg:first-of-type {
        opacity: 0;
      }
    }
  }

  // Handle deeply nested items (2nd level and beyond) - same size, ensure visibility
  .menu-content,
  .sub-items {
    .nav-link-with-favorite {
      > .favorite-star-trigger {
        left: 30px !important;
        width: 14px !important;
        height: 14px !important;
        z-index: 20; // Higher z-index for nested items

        .favorite-star-icon {
          height: 14px !important;
          width: 14px !important;
          min-width: 14px;
          min-height: 14px;
        }
      }

      // Explicitly set visibility for deeply nested hover
      &:not(.is-favorited):hover > .favorite-star-trigger {
        opacity: 1 !important;
        visibility: visible !important;
      }

      &.is-favorited > .favorite-star-trigger {
        opacity: 1 !important;
        visibility: visible !important;
      }
    }
  }
}

// CRITICAL: Override the base rule that hides SVGs in nested sub-menus
// The rule in _base-extended.scss: .nav-item.has-sub { & & li svg { display: none; } }
// hides all SVG icons in sub-sub-menus. We must override it for the star trigger.
.nav-item.has-sub .nav-item.has-sub li .favorite-star-trigger svg,
.nav-item.has-sub .nav-item.has-sub .nav-link-with-favorite > .favorite-star-trigger svg {
  display: block !important;
}

// Also ensure star trigger SVG is always displayed in any nested context
.favorite-star-trigger svg {
  display: block !important;
}

// Active state - ensure star is still visible on active items
.nav-link-with-favorite.active {
  &.is-favorited > .favorite-star-trigger {
    opacity: 1 !important;

    .favorite-star-icon.is-favorite {
      color: #ffc107 !important;
      fill: #ffc107 !important;
    }
  }
}

// Light theme
.light-layout {
  .nav-link-with-favorite > .favorite-star-trigger .favorite-star-icon {
    color: #6e6b7b;

    &.is-favorite {
      color: #ffc107 !important;
      fill: #ffc107 !important;
    }
  }
}
</style>
